import { Category, Component, ComponentField } from '@Types';
import { SpatialLayer } from '@innovyze/lib_am_common';
import {
	Drawer,
	Text,
	closeDrawer,
	UnitField,
	HTMLInputElementExtended,
	HTMLTextAreaElementExtended,
} from '@innovyze/stylovyze';
import {
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	SelectChangeEvent,
	TextField,
} from '@mui/material';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { changeSpatialProximity } from '@Actions';
import { selectCofStore, selectLofStore } from '@Selectors';
import { selectAssetFieldsStore } from '@Selectors';
import { useDispatch } from 'react-redux';
import { useStyles } from './AddNewComponent.styles';
import { useGlobalization } from '@Translations/useGlobalization';
import { ComponentScore } from './ComponentScore';

interface Props {
	forLoF: boolean;
	forInspection: boolean;
	isNew: boolean;
	assetType: string;
	category?: Category;
	component?: Component;
}

export const AddNewComponent = ({
	forLoF,
	forInspection,
	isNew,
	assetType,
	category,
	component,
}: Props) => {
	const { t } = useGlobalization();
	const classes = useStyles();
	const dispatch = useDispatch();
	const lofStore = forLoF ? selectLofStore() : null;
	const cofStore = !forLoF ? selectCofStore() : null;
	const assetStore = selectAssetFieldsStore();

	const [name, setName] = useState<string>(
		isNew && component ? component.name : '',
	);
	const [field, setField] = useState<string>(
		isNew && component && component.field ? component.field : '',
	);
	const [fieldAlias, setFieldAlias] = useState<string>(
		isNew && component && component.fieldAlias ? component.fieldAlias : '',
	);
	const [validName, setValidName] = useState<string | undefined>(undefined);

	const [step2, setStep2] = useState<boolean>(false);

	const [type, setType] = useState<string>('');
	const [dataSource, setDataSource] = useState<string>('');
	const [disableNext, setDisableNext] = useState<boolean>(true);
	const [distance, setDistance] = useState<number>(0);
	const [validDistance, setValidDistance] = useState<string>('');

	// Skip to step 2 for edit
	useEffect(() => {
		setStep2(!isNew);
	}, [isNew, component]);

	const clearFields = (changeType: boolean) => {
		if (!changeType) {
			setName('');
			setValidName(undefined);
			setType('attribute');
		}
		setField('');
		setFieldAlias('');
		setDataSource('');
		setDistance(0);
		setValidDistance('');
		setDisableNext(true);
	};

	useEffect(() => {
		clearFields(false);
	}, []);

	const fieldTypes = [
		{ value: 'attribute', label: t('Attribute'), disabled: false },
		{
			value: 'Spatial Proximity',
			label: t('Spatial Proximity'),
			disabled: false,
		},
		{
			value: 'ProximityAttribute',
			label: t('Spatial Proximity -- Attribute'),
			disabled: false,
		},
		{
			value: 'customTable',
			label: t('Custom Table'),
			disabled: false,
		},
	];

	const menuItemsInspectionLoF = lofStore?.componentInspectionFields.map(
		(item: ComponentField, index: number) => (
			<MenuItem
				key={index}
				value={item.cachedField}
				disabled={!item.isCached}>
				{item.userFriendlyName}
			</MenuItem>
		),
	);

	const menuItemsAssetLoF = lofStore?.componentFields.map(
		(item: ComponentField, index: number) => (
			<MenuItem
				key={index}
				value={item.cachedField}
				disabled={!item.isCached}>
				{item.userFriendlyName}
			</MenuItem>
		),
	);

	const menuItemsCoF = cofStore?.componentFields.map(
		(item: ComponentField, index: number) => (
			<MenuItem
				key={index}
				value={item.cachedField}
				disabled={!item.isCached}>
				{item.userFriendlyName}
			</MenuItem>
		),
	);

	const menuItemsType = fieldTypes.map(
		(
			item: { value: string; label: string; disabled: boolean },
			index: number,
		) => (
			<MenuItem key={index} value={item.value} disabled={item.disabled}>
				{item.label}
			</MenuItem>
		),
	);

	const menuItemsLayerList = () => {
		if (
			assetStore.spatialLayerList &&
			assetStore.spatialLayerList.length > 0
		) {
			return assetStore.spatialLayerList.map((layer, index: number) => (
				<MenuItem key={index} value={layer.LAYER_NAME}>
					{layer.LAYER_NAME}
				</MenuItem>
			));
		} else {
			return [];
		}
	};

	const menuItemsSpatialAttrList = () => {
		if (
			assetStore.spatialProximityAttr &&
			assetStore.spatialProximityAttr.length > 0
		) {
			const layerList = assetStore.spatialProximityAttr.map(
				layer => layer.layerName,
			);
			const uniqLayer = layerList.filter(
				(item, index) => layerList.indexOf(item) === index,
			);
			return uniqLayer.map((layer, index: number) => (
				<MenuItem key={index} value={layer}>
					{layer}
				</MenuItem>
			));
		} else {
			return [];
		}
	};

	const menuItemsSpatialAttrFieldList = () => {
		if (
			assetStore.spatialProximityAttr &&
			assetStore.spatialProximityAttr.length > 0 &&
			dataSource != ''
		) {
			const fieldList = assetStore.spatialProximityAttr
				.filter(layer => layer.layerName == dataSource)
				.map(layer => layer.cachedField);
			return fieldList.map((field2, index: number) => (
				<MenuItem key={index} value={field2}>
					{field2}
				</MenuItem>
			));
		} else {
			return [];
		}
	};

	const menuItemsCustomTableList = () => {
		if (assetStore.customTable && assetStore.customTable.length > 0) {
			const tableList = assetStore.customTable.map(
				table => table.layerName,
			);
			const uniqTable = tableList.filter(
				(item, index) => tableList.indexOf(item) === index,
			);
			return uniqTable.map((table, index: number) => (
				<MenuItem key={index} value={table}>
					{table}
				</MenuItem>
			));
		} else {
			return [];
		}
	};

	const menuItemsCustomTableFieldList = () => {
		if (
			assetStore.customTable &&
			assetStore.customTable.length > 0 &&
			dataSource != ''
		) {
			const fieldList = assetStore.customTable.filter(
				layer => layer.layerName == dataSource,
			);
			return fieldList.map((layer, index: number) => (
				<MenuItem key={index} value={layer.cachedField}>
					{layer.userFriendlyName}
				</MenuItem>
			));
		} else {
			return [];
		}
	};

	const handleFieldSelectChangeCustomTable = (
		event: SelectChangeEvent<{ name?: string; value: unknown }>,
	) => {
		const target = event?.target as HTMLInputElement;
		if (target != undefined && assetStore) {
			const result = assetStore.customTable.find(
				({ cachedField }) => cachedField === target.value,
			);
			if (result) {
				setField(result.cachedField);
				setFieldAlias(result.userFriendlyName);
			}
		} else {
			setField('');
			setFieldAlias('');
		}
	};

	const handleFieldSelectChangeSpatialAttr = (
		event: SelectChangeEvent<{ name?: string; value: unknown }>,
	) => {
		const target = event?.target as HTMLInputElement;
		if (target != undefined && assetStore) {
			const result = assetStore.spatialProximityAttr.find(
				({ cachedField }) => cachedField === target.value,
			);
			if (result) {
				setField(result.cachedField);
				setFieldAlias(result.userFriendlyName);
			}
		} else {
			setField('');
			setFieldAlias('');
		}
	};

	const handleFieldSelectChangeLoF = (
		event: SelectChangeEvent<{ name?: string; value: unknown }>,
	) => {
		const target = event?.target as HTMLInputElement;
		if (target != undefined && lofStore) {
			const result = (
				forInspection
					? lofStore.componentInspectionFields
					: lofStore.componentFields
			).find(({ cachedField }) => cachedField === target.value);
			if (result) {
				setField(result.cachedField);
				setFieldAlias(result.userFriendlyName);
			}
		} else {
			setField('');
			setFieldAlias('');
		}
	};

	const handleFieldSelectChangeCoF = (
		event: SelectChangeEvent<{ name?: string; value: unknown }>,
	) => {
		const target = event?.target as HTMLInputElement;
		if (target != undefined && cofStore) {
			const result = cofStore.componentFields.find(
				({ cachedField }) => cachedField === target.value,
			);
			if (result) {
				setField(result.cachedField);
				setFieldAlias(result.userFriendlyName);
			}
		} else {
			setField('');
			setFieldAlias('');
		}
	};

	const handleTypeSelectChange = (
		event: SelectChangeEvent<{ name?: string; value: unknown }>,
	) => {
		const target = event?.target as HTMLInputElement;
		if (target != undefined) {
			const result = fieldTypes.find(
				({ value }) => value === target.value,
			);
			if (result && result.value != type) {
				setType(result.value);
				clearFields(true);
			}
		} else {
			setField('');
			setFieldAlias('');
		}
	};

	const handleDataSourceSelectChange = (
		event: SelectChangeEvent<{ name?: string; value: unknown }>,
	) => {
		const target = event?.target as HTMLInputElement;
		if (target != undefined) {
			setDataSource(target.value);
			const sl = assetStore.spatialLayerList.find(
				s => s.LAYER_NAME === target.value,
			);
			const slm: SpatialLayer = {
				_ID: sl?._ID ?? '',
				TENANT_ID: sl?.TENANT_ID ?? '',
				LAYER_NAME: sl?.LAYER_NAME ?? '',
				EXTENT: sl?.EXTENT ?? undefined,
				GEOMETRY_TYPE: sl?.GEOMETRY_TYPE ?? undefined,
			};
			dispatch(changeSpatialProximity(slm));
		} else {
			setDataSource('');
		}
	};

	const handleDistanceChange = (
		event: ChangeEvent<
			HTMLInputElementExtended | HTMLTextAreaElementExtended
		>,
	) => {
		const distance = parseFloat(event.target.value);
		let error = '';
		if (isNaN(distance) || distance < 0) {
			error = t('Enter a valid distance');
		} else {
			setDistance(distance);
		}
		setValidDistance(error);
	};

	const validateName = (event: ChangeEvent<HTMLInputElement>) => {
		const name = event.target.value.toUpperCase();
		let error = '';
		if (name.trim().length == 0) {
			error = t('Enter a valid name');
		} else {
			let valid = true;
			if (forLoF) {
				valid =
					lofStore?.categories.find(cat =>
						cat.name === category?.name
							? cat.components.find(
									component =>
										component.name.toUpperCase() == name,
							  )
							: undefined,
					) === undefined;
			} else {
				valid =
					cofStore?.categories.find(cat =>
						cat.name === category?.name
							? cat.components.find(
									catComponent =>
										catComponent.name.toUpperCase() == name,
							  )
							: undefined,
					) === undefined;
			}

			if (!valid) error = t('Enter a unique name');
		}

		setValidName(error);
		setName(error.length == 0 ? event.target.value : '');
	};

	useEffect(() => {
		let disable = validName?.length != 0;

		if (!disable) {
			switch (type) {
				case 'attribute':
					disable = field.length === 0;
					break;
				case 'Spatial Proximity':
					disable = dataSource.length === 0;
					break;
				case 'ProximityAttribute':
					disable =
						field.length === 0 ||
						dataSource.length === 0 ||
						validDistance.length !== 0;
					break;
				case 'customTable':
					disable = field.length === 0 || dataSource.length === 0;
					break;
			}
		}

		setDisableNext(disable);
	}, [name, validName, field, type, dataSource, distance, validDistance]);

	const typeInputs = (
		<Grid xs={12} item>
			<FormControl
				variant="outlined"
				margin="normal"
				fullWidth
				disabled={!isNew}>
				<InputLabel>{t('Type')}</InputLabel>
				<Select
					data-cy="typeField"
					name="type"
					onChange={handleTypeSelectChange}
					label={t('Type')}>
					{menuItemsType}
				</Select>
			</FormControl>
		</Grid>
	);

	const attrInputs = (
		<Grid xs={12} item>
			<FormControl
				variant="outlined"
				margin="normal"
				fullWidth
				disabled={!isNew}>
				<InputLabel>{t('Field')}</InputLabel>
				<Select
					data-cy="fieldField"
					name="field"
					onChange={
						forLoF
							? handleFieldSelectChangeLoF
							: handleFieldSelectChangeCoF
					}
					label={t('Field')}>
					{forLoF
						? forInspection
							? menuItemsInspectionLoF
							: menuItemsAssetLoF
						: menuItemsCoF}
				</Select>
			</FormControl>
		</Grid>
	);

	const dataSourceSelect = () => {
		switch (type) {
			case 'Spatial Proximity':
				return menuItemsLayerList();
			case 'ProximityAttribute':
				return menuItemsSpatialAttrList();
			case 'customTable':
				return menuItemsCustomTableList();
		}
	};

	const dataSourceInputs = (
		<Grid xs={12} item>
			<FormControl
				variant="outlined"
				margin="normal"
				fullWidth
				disabled={
					type == 'ProximityAttribute' || type == 'customTable'
						? false
						: !assetStore.spatialLayerList ||
						  assetStore.spatialLayerList.length == 0
				}>
				<InputLabel>{t('Data Source')}</InputLabel>
				<Select
					data-cy="dataSourceField"
					name="dataSource"
					onChange={handleDataSourceSelectChange}
					label={t('Data Source')}>
					{dataSourceSelect()}
				</Select>
			</FormControl>
		</Grid>
	);

	const spatialAttriFieldInputs = (
		<Grid xs={12} item>
			<FormControl
				variant="outlined"
				margin="normal"
				fullWidth
				disabled={!isNew}>
				<InputLabel>{t('Field')}</InputLabel>
				<Select
					data-cy="fieldField"
					name="safield"
					onChange={handleFieldSelectChangeSpatialAttr}
					label={t('Field')}>
					{menuItemsSpatialAttrFieldList()}
				</Select>
			</FormControl>
		</Grid>
	);

	const distanceInputs = (
		<Grid xs={12} item>
			<UnitField
				fullWidth
				name="Distance"
				unit="m"
				step="1"
				min={0}
				defaultValue={distance.toString()}
				label={t('Distance')}
				variant="outlined"
				onChange={handleDistanceChange}
				helperText={validDistance}
				error={validDistance !== ''}
			/>
		</Grid>
	);

	const customTableInputs = (
		<Grid xs={12} item>
			<FormControl
				variant="outlined"
				margin="normal"
				fullWidth
				disabled={!isNew}>
				<InputLabel>{t('Field')}</InputLabel>
				<Select
					data-cy="fieldField"
					name="ctfield"
					onChange={handleFieldSelectChangeCustomTable}
					label={t('Field')}>
					{menuItemsCustomTableFieldList()}
				</Select>
			</FormControl>
		</Grid>
	);

	const inspectionInputs = attrInputs;

	const attributeInputs = (
		<>
			{typeInputs}
			{attrInputs}
		</>
	);

	const spatialProximityInputs = (
		<>
			{typeInputs}
			{dataSourceInputs}
		</>
	);

	const spatialProximityAttrInputs = (
		<>
			{typeInputs}
			{dataSourceInputs}
			{spatialAttriFieldInputs}
			{distanceInputs}
		</>
	);

	const customTableFieldsInputs = (
		<>
			{typeInputs}
			{dataSourceInputs}
			{customTableInputs}
		</>
	);

	const displayInputs = () => {
		if (forInspection) {
			return inspectionInputs;
		} else {
			switch (type) {
				case 'attribute':
					return attributeInputs;
				case 'Spatial Proximity':
					return spatialProximityInputs;
				case 'ProximityAttribute':
					return spatialProximityAttrInputs;
				case 'customTable':
					return customTableFieldsInputs;
			}
		}
	};

	const params = () => {
		return (
			<Grid container spacing={2}>
				<Grid xs={12} item>
					<TextField
						fullWidth
						required
						name="ComponentName"
						defaultValue={name}
						label={t('Component Name')}
						variant="outlined"
						onChange={validateName}
						helperText={validName ?? t('Enter a valid name')}
						error={validName !== ''}
					/>
				</Grid>
				{displayInputs()}
			</Grid>
		);
	};

	return step2 ? (
		<Paper
			style={{
				width: 'calc( 100% - 79px )',
				height: 'calc( 100% - 52px)',
				position: 'absolute',
				zIndex: 1300,
			}}>
			<ComponentScore
				assetType={assetType}
				forLoF={forLoF}
				forInspection={forInspection}
				isNew={isNew}
				category={category}
				component={
					isNew || !component
						? {
								name: name,
								type: type,
								select: false,
								weighting: 0,
								field: field,
								fieldAlias: fieldAlias,
								layer: dataSource,
								maxDistance: distance,
						  }
						: component
				}
				onApply={() => {
					dispatch(closeDrawer());
					setStep2(false);
					clearFields(false);
				}}
				onCancel={() => {
					dispatch(closeDrawer());
					setStep2(false);
					clearFields(false);
				}}
			/>
		</Paper>
	) : (
		<Drawer
			action={{
				label: t('Next'),
				onClick: function noRefCheck() {
					if (name !== '' && (field !== '' || dataSource !== '')) {
						dispatch(closeDrawer());
						setStep2(true);
					}
				},
				disabled: disableNext,
			}}
			drawerId="addNewComponentDrawer"
			onCancel={() => dispatch(closeDrawer())}
			onClose={() => clearFields(false)}
			title={isNew ? t('Add New Component') : t('Edit Component')}>
			<Text variant={'heading-medium'}>{t('Component Setup')}</Text>
			<form className={classes.formRoot} noValidate autoComplete="off">
				{params()}
			</form>
		</Drawer>
	);
};
